import React, { useState, useCallback, useEffect } from 'react';
import {
  AppModal,
  AppButton,
  AppCheckbox,
  AppTable,
  AppTextField,
  AppSpin,
  AppPagination,
} from '@/components/atoms';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { useSearchAddReturn } from '@/hooks/main/useReturnRezept';
import useAppModal from '@/hooks/useAppModal';
import type { AddReturnRezeptRow, PaginationMeta } from '@/services/main/returnRezept/types';
import type { ColumnsType } from 'antd/es/table';
import styles from './AddReturnModal.module.css';

const ALERT_NO_SELECTION = '対象のレセプトを選択してください。';
const PAGE_SIZE = 20;

interface AddReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddReturn: (rezeptIds: number[]) => void;
  isAdding: boolean;
}

export default function AddReturnModal({
  isOpen,
  onClose,
  onAddReturn,
  isAdding,
}: AddReturnModalProps) {
  const appModal = useAppModal();
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const [searchText, setSearchText] = useState('');
  const [activeSearchText, setActiveSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<AddReturnRezeptRow[] | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // HIT-14696: ids of every record the search matched (all pages) — what 全選択 selects.
  const [allIds, setAllIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { searchAddReturnMutation, isLoading: isSearching } = useSearchAddReturn();

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setActiveSearchText('');
      setSearchResults(null);
      setPagination(null);
      setSelectedIds([]);
      setAllIds([]);
      setCurrentPage(1);
    }
  }, [isOpen]);

  const fetchSearchPage = useCallback(
    (text: string, page: number, resetSelection = false) => {
      return searchAddReturnMutation({
        searchText: text,
        page,
        perPage: PAGE_SIZE,
      }).then(data => {
        if (resetSelection) {
          setSelectedIds([]);
        }
        setActiveSearchText(text);
        setSearchResults(data.rezepts);
        setAllIds(data.all_ids ?? data.rezepts.map(r => r.id));
        setPagination(data.pagination);
        setCurrentPage(data.pagination.current_page);
      });
    },
    [searchAddReturnMutation]
  );

  const handleSearch = useCallback(() => {
    fetchSearchPage(searchText, 1, true);
  }, [fetchSearchPage, searchText]);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchSearchPage(activeSearchText, page);
    },
    [activeSearchText, fetchSearchPage]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClose = useCallback(() => {
    setSearchText('');
    setActiveSearchText('');
    setSearchResults(null);
    setPagination(null);
    setSelectedIds([]);
    setAllIds([]);
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  const handleToggleRow = (id: number, checked: boolean) => {
    setSelectedIds(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };

  // HIT-14696 (UAT No168) — BD 全選択: 「DataTable内の全レコードを対象とする（表示中ページのみではなく、
  // 他ページのデータも含む）」. Used to add/remove only the ids of the page on screen.
  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? [...allIds] : []);
  };

  const handleAddReturn = () => {
    if (selectedIds.length === 0) {
      appModal.warning({ title: ALERT_NO_SELECTION, okText: 'OK' });
      return;
    }
    onAddReturn(selectedIds);
  };

  const allChecked = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));

  const columns: ColumnsType<AddReturnRezeptRow> = [
    {
      title: (
        <AppCheckbox
          checked={allChecked}
          onChange={e => handleSelectAll(e.target.checked)}
          disabled={!searchResults || searchResults.length === 0}
        />
      ),
      dataIndex: 'id',
      key: 'selection',
      width: 56,
      align: 'center',
      render: (_: unknown, record: AddReturnRezeptRow) => (
        // The row itself toggles on click (HIT-14696); keep the checkbox's own click from
        // reaching the row, otherwise one click would toggle twice.
        <span onClick={e => e.stopPropagation()}>
          <AppCheckbox
            checked={selectedIds.includes(record.id)}
            onChange={e => {
              e.stopPropagation();
              handleToggleRow(record.id, e.target.checked);
            }}
          />
        </span>
      ),
    },
    { title: '受診者番号', dataIndex: 'serial_number' },
    { title: '受診者名', dataIndex: 'patient_name' },
    { title: '保険者番号', dataIndex: 'insurer_number' },
    { title: '施術年月', dataIndex: 'treatement_yearmonth_jp' },
    { title: '提出年月', dataIndex: 'rezept_yearmonth_jp' },
    {
      title: '本関',
      dataIndex: 'rezept_kbn',
      render: (kbn: number) => (kbn === 2 ? '関' : '本'),
    },
    { title: '同意書', dataIndex: 'consent_serial_number' },
    {
      title: '請求金額',
      dataIndex: 'invoice_price',
      render: (price: number) => `￥${(price ?? 0).toLocaleString()}`,
    },
    { title: massagerLabel, dataIndex: 'massager_name' },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title="返戻を追加"
      maxWidth={searchResults && searchResults.length > 0 ? 'max-w-4xl' : 500}
      footer={
        <div className="flex justify-end gap-2">
          <AppButton onClick={handleClose}>閉じる</AppButton>
          <AppButton type="primary" onClick={handleAddReturn} loading={isAdding}>
            返戻追加
          </AppButton>
        </div>
      }
    >
      <div className="mb-4 flex gap-2 items-center w-full">
        <label className="whitespace-nowrap text-sm font-medium">受診者検索</label>
        <AppTextField
          className="w-full"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="受診者名または受診者番号"
          maxLength={255}
        />
        <AppButton type="primary" onClick={handleSearch} className="w-32" loading={isSearching}>
          検索
        </AppButton>
      </div>

      {isSearching && (
        <div className="flex flex-col items-center justify-center py-8">
          <AppSpin tip="処理中" />
        </div>
      )}

      {searchResults !== null && !isSearching && (
        <>
          {searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">該当する検索結果がありません。</div>
          ) : (
            <>
              <div className={styles.addReturnTable}>
                <AppTable<AddReturnRezeptRow>
                  columns={columns}
                  dataSource={searchResults}
                  rowKey="id"
                  bordered
                  pagination={false}
                  // HIT-14696 — BD 各行checkbox: Check時 行背景色 skyblue / Uncheck時 白,
                  // 行内の任意位置クリックでもcheckboxをtoggle可能とする.
                  rowClassName={record =>
                    selectedIds.includes(record.id) ? styles.rowChecked : styles.rowUnchecked
                  }
                  onRow={record => ({
                    onClick: () => handleToggleRow(record.id, !selectedIds.includes(record.id)),
                    style: { cursor: 'pointer' },
                  })}
                />
              </div>
              {pagination && pagination.total > pagination.per_page && (
                <AppPagination
                  className="mt-2"
                  current={currentPage}
                  total={pagination.total}
                  pageSize={pagination.per_page}
                  disabled={isSearching}
                  onChange={handlePageChange}
                />
              )}
            </>
          )}
        </>
      )}
    </AppModal>
  );
}
